import { useState, useEffect, useCallback } from 'react';
import { useMentiSession } from '../../../context/MentiSessionContext';
import { getPresentation } from '../../../services/mentiService';
import { advanceSlide } from '../../../services/mentiSessionService';
import SlideRenderer from '../display/SlideRenderer';

export default function MentiPresenter({ onExit }) {
  const { session, loading: sessionLoading } = useMentiSession();
  const [presentation, setPresentation] = useState(null);
  const [presLoading, setPresLoading] = useState(true);

  // Fetch presentation data
  useEffect(() => {
    if (!session?.presentationId) return;
    setPresLoading(true);
    getPresentation(session.presentationId)
      .then((data) => {
        setPresentation(data);
        setPresLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching presentation:', err);
        setPresLoading(false);
      });
  }, [session?.presentationId]);

  const slides = presentation?.slides || [];
  const totalSlides = slides.length;
  const currentIndex = session?.currentSlideIndex || 0;
  const currentSlide = slides[currentIndex] || null;
  const participants = session?.participants ? Object.keys(session.participants).length : 0;

  const handleNext = useCallback(async () => {
    if (!session?.id || currentIndex >= totalSlides - 1) return;
    try {
      await advanceSlide(session.id, totalSlides, 1);
    } catch (err) {
      console.error('Error advancing slide:', err);
    }
  }, [session?.id, currentIndex, totalSlides]);

  const handlePrev = useCallback(async () => {
    if (!session?.id || currentIndex <= 0) return;
    try {
      await advanceSlide(session.id, totalSlides, -1);
    } catch (err) {
      console.error('Error going to previous slide:', err);
    }
  }, [session?.id, currentIndex, totalSlides]);

  const handleExit = useCallback(() => {
    onExit();
  }, [onExit]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault();
          handleNext();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          handlePrev();
          break;
        case 'Escape':
          e.preventDefault();
          handleExit();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrev, handleExit]);

  if (sessionLoading || presLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white/60 font-medium text-lg">Lastar presentasjon...</div>
      </div>
    );
  }

  if (!presentation || slides.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center text-center p-6">
        <span className="material-symbols-outlined text-6xl text-white/30 mb-4">layers</span>
        <h3 className="text-xl font-bold text-white/80 mb-2">Ingen lysbilete funne</h3>
        <p className="text-white/50 mb-6">Presentasjonen har ingen lysbilete.</p>
        <button
          onClick={onExit}
          className="px-6 py-3 bg-white/10 text-white rounded-xl font-bold hover:bg-white/20 transition-colors"
        >
          Ga tilbake
        </button>
      </div>
    );
  }

  // Get responses for current slide
  const slideResponses = currentSlide ? (session?.responses?.[currentSlide.id] || {}) : {};

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col select-none">
      {/* Main slide area */}
      <div className="flex-1 flex items-center justify-center p-8">
        {currentSlide ? (
          <SlideRenderer
            slide={currentSlide}
            responses={slideResponses}
            participantCount={participants}
          />
        ) : (
          <div className="text-white/40 text-xl font-medium">Ingen lysbilete</div>
        )}
      </div>

      {/* Bottom bar */}
      <div className="bg-gray-800/80 backdrop-blur-sm border-t border-white/5 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={handlePrev}
            disabled={currentIndex <= 0}
            className="flex items-center justify-center size-10 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Forrige lysbilde"
          >
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <button
            onClick={handleNext}
            disabled={currentIndex >= totalSlides - 1}
            className="flex items-center justify-center size-10 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Neste lysbilde"
          >
            <span className="material-symbols-outlined">arrow_forward</span>
          </button>
        </div>

        {/* Slide counter */}
        <div className="text-white/60 font-bold text-sm">
          {currentIndex + 1} / {totalSlides}
        </div>

        <div className="flex items-center gap-4">
          {/* Participant count */}
          <div className="flex items-center gap-1.5 text-white/50 text-sm">
            <span className="material-symbols-outlined text-[18px]">group</span>
            <span className="font-medium">{participants}</span>
          </div>

          {/* Exit button */}
          <button
            onClick={handleExit}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white/70 rounded-xl text-sm font-bold hover:bg-white/20 hover:text-white transition-colors"
            title="Avslutt presentasjonen (ESC)"
          >
            <span className="material-symbols-outlined text-[16px]">close</span>
            ESC
          </button>
        </div>
      </div>
    </div>
  );
}
